import { useEffect, useRef } from 'react';

/**
 * A short-lived page cache for the Player Database's three lists, and the background prefetch that
 * fills it, so Next and a tab switch show results at once.
 *
 * - Every page the coach loads is kept for `PAGE_CACHE_TTL_MS`, keyed by the list, its filters,
 *   the page and the page size. Going back to a page within that time asks the API nothing.
 * - Once a page has settled, the view prefetches the next `PREFETCH_NEXT_PAGES` pages and the first
 *   page of the other tabs, one request at a time, after `PREFETCH_DELAY_MS`.
 * - The API limits these reads to 120 a minute per user (`directory-player-reads`). Prefetch spends
 *   at most `PREFETCH_PER_MINUTE` of them in any minute, across every list, and stops for a minute
 *   after a 429, so what the coach clicks never waits on a prefetch.
 * - A first page cached at a larger size answers a smaller first page (the same rows, cut), so a tab
 *   whose fitted size is not known yet can be prefetched at `MAX_PREFETCH_PAGE_SIZE`.
 * - Every request still writes the server's access log, prefetch included: the rows reach the
 *   browser either way. A page served from the cache writes nothing.
 */

export const PAGE_CACHE_TTL_MS = 2 * 60_000;
export const PREFETCH_NEXT_PAGES = 4;
export const PREFETCH_PER_MINUTE = 30;
export const PREFETCH_DELAY_MS = 400;
export const PREFETCH_429_PAUSE_MS = 60_000;
/** The API's page size ceiling, used for a tab whose page size is only known once it is on screen. */
export const MAX_PREFETCH_PAGE_SIZE = 100;
const MAX_ENTRIES = 80;

export interface Paged {
  items: readonly unknown[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/** A list query with its page and page size; the filters are everything else. */
export interface PageQuery {
  page?: number;
  pageSize?: number;
}

interface Entry {
  value: Paged;
  at: number;
}

const settings = { enabled: true, now: () => Date.now() };
const entries = new Map<string, Entry>();
const inflight = new Map<string, Promise<Paged>>();
let prefetchTimes: number[] = [];
let pausedUntil = 0;
/** Bumped by every reset, so a request sent before it never writes its answer into the new cache. */
let generation = 0;

/** Test seam: turn the cache and prefetch off (the default under test) or on, and set the clock. */
export const configurePagePrefetch = (next: { enabled?: boolean; now?: () => number }) => {
  if (next.enabled !== undefined) settings.enabled = next.enabled;
  if (next.now) settings.now = next.now;
};

/**
 * Forget every cached page and every request on its way. Rows carry the caller's own program's data
 * ("In your players"), so AuthContext calls this on sign-out and on every organization switch, and an
 * Add calls it so a page shown again reads the new state from the API.
 */
export const invalidatePageCache = () => {
  generation += 1;
  entries.clear();
  inflight.clear();
};

/** Test seam: `invalidatePageCache` plus the prefetch budget. */
export const resetPageCache = () => {
  invalidatePageCache();
  prefetchTimes = [];
  pausedUntil = 0;
};

/** `ApiError.statusCode`, read by shape so this module (and the test setup) never loads the API client. */
const isTooManyRequests = (err: unknown) => (err as { statusCode?: unknown } | null)?.statusCode === 429;

const entryKey = (list: string, filters: string, page: number, pageSize: number) => `${list}|${filters}|${page}|${pageSize}`;

const fresh = (entry: Entry | undefined): entry is Entry =>
  !!entry && settings.now() - entry.at < PAGE_CACHE_TTL_MS;

/** A page the cache can answer: this exact page, or a first page cached at a larger size, cut down. */
const cached = (list: string, filters: string, page: number, pageSize: number): Paged | undefined => {
  const exact = entries.get(entryKey(list, filters, page, pageSize));
  if (fresh(exact)) return exact.value;
  if (page !== 1) return undefined;
  const prefix = `${list}|${filters}|1|`;
  for (const [key, entry] of entries) {
    if (!key.startsWith(prefix) || !fresh(entry)) continue;
    if (entry.value.pageSize >= pageSize) {
      return { ...entry.value, items: entry.value.items.slice(0, pageSize), pageSize };
    }
  }
  return undefined;
};

const store = (key: string, value: Paged) => {
  entries.delete(key);
  entries.set(key, { value, at: settings.now() });
  while (entries.size > MAX_ENTRIES) {
    const oldest = entries.keys().next().value;
    if (oldest === undefined) break;
    entries.delete(oldest);
  }
};

const abortError = () => new DOMException('The request was aborted.', 'AbortError');

/** Waits for `promise`, or rejects as soon as `signal` fires, without cancelling the shared request. */
const followed = <T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> => {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
};

/** Takes one prefetch from the per-minute budget, or says the budget is spent. */
const takePrefetchSlot = (): boolean => {
  const now = settings.now();
  if (now < pausedUntil) return false;
  prefetchTimes = prefetchTimes.filter((t) => now - t < 60_000);
  if (prefetchTimes.length >= PREFETCH_PER_MINUTE) return false;
  prefetchTimes.push(now);
  return true;
};

export interface PagedList<Q extends PageQuery, R extends Paged> {
  /** The page for `query`: from the cache, from a request already on its way, or from the API. */
  load: (query: Q, signal?: AbortSignal) => Promise<R>;
  /** Fetches `query` into the cache in the background. False when it was not asked for (cached, off, no budget). */
  prefetch: (query: Q) => Promise<boolean>;
  /** Whether the cache already holds `query`'s page. */
  has: (query: Q) => boolean;
}

/**
 * One list's cache-aware loader. `paramsOf` is the list's own query string builder (it leaves the
 * page and page size off when they are undefined, so the rest is the filter key).
 */
export const pagedList = <Q extends PageQuery, R extends Paged>(
  list: string,
  paramsOf: (query: Q) => URLSearchParams,
  fetchPage: (query: Q, signal?: AbortSignal) => Promise<R>
): PagedList<Q, R> => {
  const parts = (query: Q) => ({
    filters: paramsOf({ ...query, page: undefined, pageSize: undefined }).toString(),
    page: query.page ?? 1,
    pageSize: query.pageSize ?? 0,
  });

  // The shared request is never given a caller's signal: one caller giving up (a re-render, a click
  // on a row while a page loads, React's StrictMode double effect) must not fail the others waiting
  // on it. Each caller follows it with its own signal; an abandoned request finishes and fills the cache.
  const request = (query: Q, signal?: AbortSignal): Promise<R> => {
    const { filters, page, pageSize } = parts(query);
    const key = entryKey(list, filters, page, pageSize);
    const pending = inflight.get(key);
    if (pending) return followed(pending as Promise<R>, signal);
    const sentIn = generation;
    const promise = fetchPage(query).then(
      (value) => {
        if (sentIn === generation) store(key, value);
        return value;
      },
      (err: unknown) => {
        // A 429 on any request, the coach's own included, pauses prefetch: the limit is already spent.
        if (isTooManyRequests(err)) pausedUntil = settings.now() + PREFETCH_429_PAUSE_MS;
        throw err;
      }
    );
    inflight.set(key, promise);
    const clear = () => {
      if (inflight.get(key) === promise) inflight.delete(key);
    };
    promise.then(clear, clear);
    return followed(promise, signal);
  };

  return {
    load: (query, signal) => {
      if (!settings.enabled) return fetchPage(query, signal);
      // `fetchPage` is the list's service call; with the cache on it never sees a caller's signal.
      const { filters, page, pageSize } = parts(query);
      const hit = cached(list, filters, page, pageSize);
      return hit ? Promise.resolve(hit as R) : request(query, signal);
    },
    prefetch: async (query) => {
      if (!settings.enabled) return false;
      const { filters, page, pageSize } = parts(query);
      if (cached(list, filters, page, pageSize) || inflight.has(entryKey(list, filters, page, pageSize))) return false;
      if (!takePrefetchSlot()) return false;
      try {
        await request(query);
        return true;
      } catch {
        // A 429 has already paused prefetch in `request`; anything else loads normally when asked for.
        return false;
      }
    },
    has: (query) => {
      const { filters, page, pageSize } = parts(query);
      return !!cached(list, filters, page, pageSize);
    },
  };
};

/** The pages after `page`, at most `PREFETCH_NEXT_PAGES` and never past the last one. */
export const nextPages = (page: number, pageSize: number, totalCount: number): number[] => {
  if (pageSize <= 0) return [];
  const last = Math.ceil(totalCount / pageSize);
  const pages: number[] = [];
  for (let p = page + 1; p <= Math.min(last, page + PREFETCH_NEXT_PAGES); p++) pages.push(p);
  return pages;
};

/** One background request: a key that names it, and the call. */
export interface PrefetchJob {
  key: string;
  run: () => Promise<unknown>;
}

/**
 * Runs `jobs` one after another, `PREFETCH_DELAY_MS` after they are `ready`, and stops when the
 * list of jobs changes (a new page or new filters) or the view goes away. A request already sent is
 * left to finish: it fills the cache, and the next view may want it.
 */
export const usePrefetch = (jobs: readonly PrefetchJob[], ready: boolean) => {
  const jobsRef = useRef(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  });
  const signature = ready && settings.enabled ? jobs.map((job) => job.key).join('\n') : '';

  useEffect(() => {
    if (!signature) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        for (const job of jobsRef.current) {
          if (cancelled) return;
          try {
            await job.run();
          } catch {
            // A prefetch that fails costs nothing: the page loads normally when the coach asks for it.
          }
        }
      })();
    }, PREFETCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [signature]);
};
